"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.throwIfNotValidDeleteAfter = exports.MAX_DELETE_AFTER = exports.MIN_DELETE_AFTER = exports.vpnIdToWgFileName = exports.getVpnConfigTypes = exports.getVpnFrom = exports.newVpn = exports.rank = exports.VpnType = exports.VpnState = void 0;
const errors_1 = require("../errors");
var VpnState;
(function (VpnState) {
    VpnState["Failed"] = "Failed";
    VpnState["Creating"] = "Creating";
    VpnState["Created"] = "Created";
    VpnState["Provisioning"] = "Provisioning";
    VpnState["Paused"] = "Paused";
    VpnState["Running"] = "Running";
    VpnState["Deprovisioning"] = "Deprovisioning";
    VpnState["Deleted"] = "Deleted";
})(VpnState || (VpnState = {}));
exports.VpnState = VpnState;
const vpnStateRank = Object.values(VpnState);
function rank(state) {
    return vpnStateRank.indexOf(state);
}
exports.rank = rank;
var VpnType;
(function (VpnType) {
    VpnType["WireGuard"] = "wireguard";
})(VpnType || (VpnType = {}));
exports.VpnType = VpnType;
const MIN_DELETE_AFTER = 5 * 60; // Minimum is 5 minutes
exports.MIN_DELETE_AFTER = MIN_DELETE_AFTER;
const MAX_DELETE_AFTER = 24 * 60 * 60; // Maximum is 24 hours
exports.MAX_DELETE_AFTER = MAX_DELETE_AFTER;
function getVpnConfigTypes() {
    return Object.values(VpnType).filter((item) => {
        return isNaN(Number(item));
    });
}
exports.getVpnConfigTypes = getVpnConfigTypes;
function throwIfNotValidVpnId(vpnId) {
    // e.g: YYYYMMDDHHmmssmss@az-test-7
    if (!/\d{4}[01]\d[0-3]\d[0-2]\d[0-5]\d[0-5]\d\d{3}@[a-z]{2}-[a-z]+-\d/.test(vpnId)) {
        throw new errors_1.MyTmpVpnError(`Incorrect vpnId: \'${vpnId}\'`);
    }
}
function throwIfNotValidDeleteAfter(deleteAfter) {
    if (deleteAfter === undefined) {
        return;
    }
    if (isNaN(deleteAfter)) {
        return;
    }
    if (!isFinite(deleteAfter)) {
        throw new errors_1.MyTmpVpnError(`deleteAfter must be finite: ${deleteAfter}`);
    }
    if (deleteAfter < MIN_DELETE_AFTER) {
        throw new errors_1.MyTmpVpnError(`deleteAfter must be greater than ${MIN_DELETE_AFTER}: ${deleteAfter}`);
    }
    if (deleteAfter > MAX_DELETE_AFTER) {
        throw new errors_1.MyTmpVpnError(`deleteAfter must be lesser than ${MAX_DELETE_AFTER}: ${deleteAfter}`);
    }
}
exports.throwIfNotValidDeleteAfter = throwIfNotValidDeleteAfter;
function newVpn(region, config, state) {
    // e.g: ap-northeast-3
    if (!/[a-z]{2}-[a-z]+-[1-9]+/.test(region)) {
        throw new errors_1.MyTmpVpnError(`Incorrect region: \'${region}\'`);
    }
    const createdAt = new Date();
    const vpnId = `${dateToId(createdAt)}@${region}`;
    // Assert we will be able to deserialize later on...
    throwIfNotValidVpnId(vpnId);
    throwIfNotValidDeleteAfter(config.deleteAfter);
    return {
        vpnId,
        createdAt,
        region,
        config,
        state
    };
}
exports.newVpn = newVpn;
function getVpnFrom(vpnId, state, config) {
    throwIfNotValidVpnId(vpnId);
    throwIfNotValidDeleteAfter(config.deleteAfter);
    const tokens = vpnId.split('@');
    if (tokens.length !== 2) {
        throw new errors_1.MyTmpVpnError(`Incorrect vpnId: ${vpnId}`);
    }
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const createdAt = idToDate(tokens[0]);
    const region = tokens[1];
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    return {
        vpnId,
        createdAt,
        region,
        config,
        state
    };
}
exports.getVpnFrom = getVpnFrom;
// Using a date from an id is convenient for debugging.
// We can't use Date.toISOString() because some characters are forbidden
// E.g: SFn refuse the ':'
// So we need convertor back and forth
function dateToId(date) {
    // YYYY-MM-DDTHH:mm:ss.uuuZ -> YYYYMMDDHHmmssuuu
    return date.toISOString().replace(/[^\d]/g, '');
}
function idToDate(id) {
    // YYYYMMDDHHmmssuuu -> YYYY-MM-DDTHH:mm:ss.uuuZ
    const year = id.substring(0, 4);
    const month = id.substring(4, 6);
    const day = id.substring(6, 8);
    const hour = id.substring(8, 10);
    const minute = id.substring(10, 12);
    const second = id.substring(12, 14);
    const millis = id.substring(14);
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}.${millis}Z`);
}
// The WireGuard Mobile Application has requirements for the wireguard configuration file:
// 1. must have extension '.conf'
// 2. file name must match the following regexp: "[a-zA-Z0-9_=+.-]{1,15}")
// https://git.zx2c4.com/wireguard-android/tree/tunnel/src/main/java/com/wireguard/android/backend/Tunnel.java#n19
// This function is to transform from vpnIn to Wireguard mobile application requirements
function vpnIdToWgFileName(vpnId) {
    throwIfNotValidVpnId(vpnId);
    // The vpnId is made of YYYYMMDDHHmmssmss@region
    // Let's keep it simple: vpn is mostly temporary, so keeping 
    // the year is useless. 
    // Also the milliseconds are useless.
    // Finally, the region itself is of the following format: ap-northeast-3
    // We can compressing with something like:
    // MMDDHHmmssapne3.conf
    const ddhhmmss = vpnId.substring(4, 14);
    const seps = vpnId.substring(18).split(/-/);
    const ccld = seps[0];
    let location;
    switch (seps[1]) {
        case 'north':
            location = 'n';
            break;
        case 'northeast':
            location = 'ne';
            break;
        case 'east':
            location = 'e';
            break;
        case 'southeast':
            location = 'se';
            break;
        case 'south':
            location = 's';
            break;
        case 'southwest':
            location = 'sw';
            break;
        case 'west':
            location = 'w';
            break;
        case 'northwest':
            location = 'nw';
            break;
        case 'central':
            location = 'c';
            break;
        default:
            '';
            break;
    }
    const number = seps[2];
    // Let's make sure the end file is of 15 char max
    const result = `${ddhhmmss}${ccld}${location}${number}`.substring(0, 15);
    console.log(`Transforming ${vpnId} to ${result}`);
    return result;
}
exports.vpnIdToWgFileName = vpnIdToWgFileName;
